import React from "react";

interface State {
  hasError: boolean;
  error?: Error;
}

const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk \d+ failed|Loading CSS chunk/i;

const isChunkError = (err: unknown): boolean => {
  if (!err) return false;
  const msg = err instanceof Error ? err.message : String(err);
  return CHUNK_ERROR_RE.test(msg);
};

const reloadOnce = () => {
  try {
    const key = "lov_chunk_reload_at";
    const last = Number(sessionStorage.getItem(key) || "0");
    if (Date.now() - last < 10_000) return; // don't loop
    sessionStorage.setItem(key, String(Date.now()));
    window.location.reload();
  } catch {
    window.location.reload();
  }
};

class ErrorBoundary extends React.Component<React.PropsWithChildren, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error) {
    if (isChunkError(error)) {
      reloadOnce();
    }
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error);
  }

  render() {
    if (this.state.hasError) {
      if (isChunkError(this.state.error)) {
        return null; // reloading
      }
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#1e1e3c] text-white p-6">
          <div className="max-w-md text-center">
            <h1 className="text-2xl font-bold mb-3">Something went wrong</h1>
            <p className="text-white/70 mb-6 text-sm">
              The page failed to load. This is usually fixed by reloading.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="h-11 px-6 rounded-xl bg-[#E91E63] hover:bg-[#d81b60] transition-colors font-semibold"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
export { isChunkError, reloadOnce };
